import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import {
  useUpdateUserPhoneSheetFiltersMutation,
  UserPhoneSheetFilterDocument,
} from '../generated/graphql';
import { useDeskQuery } from '../generated/graphql';
import { useDeskStatusesQuery } from '../generated/graphql';
import { useUserPhoneSheetFilterQuery } from '../generated/graphql';

// Properties that should be treated as numbers in query params
const numericProperties = ['favorite'];
// Properties that should be treated as arrays in query params
const arrayProperties = ['filter'];

/**
 * Converts an object to a URL query string
 * @param {Object} params - The parameters to convert
 * @param {Boolean} parseArrays - Whether to join arrays into comma-separated strings
 * @returns {String} URL-encoded query string
 */
const objectToQueryString = (params, parseArrays) => {
  if (!params) {
    return '';
  }

  Object.entries(params).forEach(([key, value]) => {
    if (parseArrays && arrayProperties?.includes(key) && Array.isArray(value)) {
      params[key] = value?.join(',');
    }

    if (numericProperties?.includes(key)) {
      if (isNaN(params[key])) {
        delete params[key];
      } else {
        params[key] = String(value);
      }
    }
  });

  const sortedParams = Object.keys(params)
    .sort()
    .reduce((obj, key) => {
      obj[key] = params[key];
      return obj;
    }, {});

  return new URLSearchParams(sortedParams).toString();
};

/**
 * Converts a URL query string to an object
 * @param {String} queryString - The query string to parse
 * @param {Boolean} parseArrays - Whether to split comma-separated values into arrays
 * @returns {Object} Parsed object with proper types
 */
const queryStringToObject = (queryString, parseArrays) => {
  const obj = Object.fromEntries(new URLSearchParams(queryString));

  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (parseArrays && arrayProperties?.includes(key)) {
      return {
        ...acc,
        [key]: value?.split(/%2C|,/g),
      };
    }

    if (numericProperties?.includes(key)) {
      value = typeof value === 'string' ? Number(value) : value;
      if (isNaN(value)) {
        value = undefined;
      }

      return {
        ...acc,
        [key]: value,
      };
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {});
};

/**
 * Removes empty, null, undefined, and invalid values from an object
 * Also filters out empty items from arrays
 * @param {Object} obj - The object to clean
 * @returns {Object} Object with empty values removed
 */
const removeEmpties = (obj) => {
  Object.entries(obj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value = value.filter(
        (item) =>
          !!item?.length &&
          ![null, undefined, '', 'null', 'undefined'].includes(item) &&
          (typeof value !== 'number' || !isNaN(value))
      );
      obj[key] = value;
    }

    if (
      [null, undefined, '', 'null', 'undefined'].includes(value) ||
      (Array.isArray(value) && !value?.length) ||
      (typeof value === 'number' && isNaN(value))
    ) {
      delete obj[key];
    }
  });

  return obj;
};

/**
 * Custom hook to manage synchronization between URL query parameters, local state, and backend storage
 *
 * This hook handles:
 * - Syncing URL query params with user's saved filters in the database
 * - Validating and cleaning filters based on current desk statuses
 * - Detecting status renames and updating filters accordingly
 * - Managing desk selection, search query, and favorite filters
 *
 * @param {String} userId - The current user's ID
 * @param {Array<String>} deskIds - Array of desk IDs the user has access to
 * @param {Array<Object>} initialPhoneSheetFilters - User's saved filters from the database
 * @param {Boolean} loggedIn - Whether the user is logged in
 *
 * @returns {Object} Object containing:
 *   - desk: Current desk object
 *   - search: Current search parameters (query, deskId, favorite, filter)
 *   - deskStatuses: Array of status objects for the current desk
 *   - deskStatusesDeskId: Desk the loaded deskStatuses belong to (lags deskId during a switch)
 *   - changeDesk: Function to change the selected desk
 *   - updateQuery: Function to update the search query
 *   - refreshStore: Function to refetch desk, statuses, and filters from backend
 *   - reloadDeskStatuses: Function to refetch only the current desk's statuses
 *   - updateParams: Function to update URL parameters and sync with backend
 */
export default function useQueryParamsSync({
  userId,
  deskIds,
  initialPhoneSheetFilters,
  loggedIn,
}) {
  // States - deskId needs to be defined early for query variables
  const [query, setQuery] = useState();
  const [deskId, setDeskId] = useState();
  const [filters, setFilters] = useState([]);
  const [favorite, setFavorite] = useState();
  const [desksFilters, setDesksFilters] = useState(initialPhoneSheetFilters);
  const [loadedStatuses, setLoadedStatuses] = useState({});
  const { statuses: deskStatuses, deskId: deskStatusesDeskId } = loadedStatuses;
  const latestStatusesRequest = useRef(0);
  const statusesInFlight = useRef(false);
  const reloadRequested = useRef(false);
  const previousDeskStatuses = useRef();

  // Queries - useDeskQuery now watches the Apollo cache for updates
  const { data: deskData, refetch: refetchDesk } = useDeskQuery({
    variables: { id: deskId },
    skip: !loggedIn || !deskId,
    fetchPolicy: 'cache-and-network',
  });
  const { refetch: refetchUserFilters } = useUserPhoneSheetFilterQuery({
    skip: true,
  });
  const { refetch: refetchDeskStatuses } = useDeskStatusesQuery({
    skip: true,
  });

  // Mutations
  const [updatePhoneSheetFilters] = useUpdateUserPhoneSheetFiltersMutation({
    refetchQueries: [UserPhoneSheetFilterDocument],
    awaitRefetchQueries: true,
  });

  // Derive desk from Apollo cache data (creates a mutable copy to avoid GraphQL readonly issues)
  const desk = useMemo(() => {
    if (!deskData?.desks?.[0]) {
      return undefined;
    }
    return JSON.parse(JSON.stringify(deskData.desks[0]));
  }, [deskData]);

  /**
   * Fetches desk data from the backend
   * Now uses refetch to trigger Apollo cache update which will propagate to the desk memo
   */
  const fetchDesk = useCallback(
    (_deskId) => {
      if (!loggedIn) {
        return;
      }
      return refetchDesk({ id: _deskId });
    },
    [loggedIn, refetchDesk]
  );

  /**
   * Fetches desk statuses from the backend
   * Creates a mutable copy of the statuses array to avoid GraphQL readonly issues
   */
  const refetchStatuses = useCallback(
    (deskId) => {
      if (!loggedIn) {
        return;
      }
      const request = ++latestStatusesRequest.current;
      statusesInFlight.current = true;
      const load = () => refetchDeskStatuses({ id: deskId });
      load()
        .catch(load)
        .then(({ data }) => {
          // Requests can resolve out of order (e.g. a quick desk switch); keep only the latest.
          if (request !== latestStatusesRequest.current) {
            return;
          }
          const notReadonlyStatuses = JSON.parse(JSON.stringify(data?.desks?.[0]?.statuses));
          reloadRequested.current = false;
          setLoadedStatuses({ deskId, statuses: notReadonlyStatuses });
        })
        .catch((error) => {
          console.error('Failed to load desk statuses:', error);
          if (request === latestStatusesRequest.current && reloadRequested.current) {
            reloadRequested.current = false;
            refetchStatuses(deskId);
          }
        })
        .finally(() => {
          if (request === latestStatusesRequest.current) {
            statusesInFlight.current = false;
          }
        });
    },
    [loggedIn, refetchDeskStatuses]
  );

  useEffect(() => {
    if (!deskFilters) {
      setDesksFilters(initialPhoneSheetFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPhoneSheetFilters]);

  useEffect(() => {
    if (!deskId && deskIds?.length) {
      const searchParams = new URLSearchParams(window.location.search);
      const searchDeskId = searchParams?.get('deskId');

      const firstDeskId = deskIds?.[0];
      const foundDeskId = deskIds?.find((_deskId) => _deskId === searchDeskId);

      if (!foundDeskId) {
        window.history.replaceState(undefined, '', '/');
      }

      const newDeskId = foundDeskId || firstDeskId;

      if (newDeskId) {
        setDeskId(newDeskId);
      }
    }
  }, [deskId, deskIds]);

  /**
   * Refreshes all data from the backend (desk, statuses, and user filters)
   * Called when desk changes or after mutations to ensure data is up-to-date
   */
  const refreshStore = useCallback(() => {
    if (!loggedIn) {
      return;
    }
    fetchDesk(deskId);
    refetchStatuses(deskId);
    refetchUserFilters({
      id: userId,
    }).then((response) => {
      const phoFilterResponse = response?.data?.users?.[0]?.phoneSheetFilter;
      const notReadonlyPhoFilter = JSON.parse(JSON.stringify(phoFilterResponse));
      setDesksFilters(notReadonlyPhoFilter);
    });
  }, [deskId, fetchDesk, loggedIn, refetchStatuses, refetchUserFilters, userId]);

  useEffect(() => {
    if (deskId) {
      refreshStore();
    }
  }, [deskId, refreshStore]);

  // A new request supersedes the one in flight and could discard a good response, so while one
  // is in flight the reload only runs if that request fails.
  const reloadDeskStatuses = useCallback(() => {
    if (statusesInFlight.current) {
      reloadRequested.current = true;
    } else {
      refetchStatuses(deskId);
    }
  }, [deskId, refetchStatuses]);

  const search = useMemo(() => {
    const obj = {
      query,
      deskId,
      favorite,
      filter: filters,
    };

    return removeEmpties(obj);
  }, [deskId, favorite, filters, query]);

  const deskFilters = useMemo(() => {
    const foundDeskFilters = desksFilters?.find((deskFilters) => deskFilters.deskId === deskId);
    if (foundDeskFilters) {
      return removeEmpties(queryStringToObject(objectToQueryString(foundDeskFilters, true), true));
    }

    return undefined;
  }, [deskId, desksFilters]);

  /**
   * MAIN SYNC EFFECT: Synchronizes URL query params with stored filters and local state
   *
   * This effect runs when deskId or deskStatuses change and handles 3 cases:
   *
   * CASE 1: No URL params
   *   - Load saved filters from backend (deskFilters)
   *   - Update local state
   *   - Update URL with saved filters
   *
   * CASE 2: URL params differ from saved filters (URL is the source of truth)
   *   - Validate filters against current deskStatuses
   *   - Remove any invalid/deleted status filters
   *   - Save cleaned filters to backend
   *   - Update local state with cleaned filters
   *   - Update URL with cleaned filters
   *
   * CASE 3: URL params match saved filters (double check)
   *   - Update local state from saved filters
   *   - Ensure URL reflects saved filters
   */
  useEffect(() => {
    if (!deskFilters || !deskStatuses || String(deskStatusesDeskId) !== String(deskId)) {
      return;
    }

    // Validate filters against current desk statuses to remove stale/invalid entries
    const cleanFilters = (filtersToClean) => {
      if (!filtersToClean?.length) {
        return filtersToClean;
      }
      const validStatusNames = deskStatuses.map((s) => s.status);
      const validFilters = filtersToClean.filter((f) => validStatusNames.includes(f));
      return validFilters.length > 0 ? validFilters : undefined;
    };

    let searchObj = removeEmpties(queryStringToObject(window.location.search, true));

    if (!window.location?.search?.length) {
      // CASE 1: No URL params - Load from store
      const cleanedFilter = cleanFilters(deskFilters?.filter);
      const resolvedFilters = removeEmpties({
        ...deskFilters,
        deskId: deskFilters?.deskId ?? deskId,
        filter: cleanedFilter,
      });
      setFilters(resolvedFilters.filter);
      setDeskId(resolvedFilters.deskId);
      setFavorite(resolvedFilters.favorite);
      setQuery(resolvedFilters.query);

      window.history.replaceState(
        resolvedFilters,
        '',
        `?${objectToQueryString(resolvedFilters, true)}`
      );
    } else if (!isEqual(searchObj, deskFilters)) {
      // CASE 2: URL is different - URL is source of truth, validate and save

      // Validate and clean filters based on current desk statuses BEFORE saving to DB
      if (searchObj.filter?.length) {
        const cleaned = cleanFilters(searchObj.filter);
        if (cleaned?.length !== searchObj.filter?.length) {
          searchObj = {
            ...searchObj,
            filter: cleaned,
          };
          searchObj = removeEmpties(searchObj);
        }
      }

      // Change arrays to be comma-separated strings to match the expected API form
      const stateWithoutArrays = removeEmpties(
        queryStringToObject(objectToQueryString({ ...searchObj }, true))
      );

      // Save cleaned filters to backend
      if (loggedIn) {
        updatePhoneSheetFilters({
          variables: {
            input: {
              phoneSheetFilters: [stateWithoutArrays],
            },
          },
        }).then((result) => {
          setDesksFilters(result?.data?.updatePhoneSheetFilters?.phoneSheetFilter);
        });
      }

      // Update local state with cleaned filters
      setFilters(searchObj.filter);
      setDeskId(searchObj.deskId ?? deskId);
      setFavorite(searchObj?.favorite);
      setQuery(searchObj?.query);

      // Update URL with cleaned searchObj
      const cleanedQueryString = objectToQueryString(searchObj, true);
      window.history.replaceState(searchObj, '', `?${cleanedQueryString}`);
    } else {
      // CASE 3: URL matches store - Just update local state
      const cleanedFilter = cleanFilters(deskFilters?.filter);
      setFilters(cleanedFilter);
      setDeskId(deskFilters?.deskId ?? deskId);
      setFavorite(deskFilters?.favorite);
      setQuery(deskFilters?.query);

      const cleanedDeskFilters = removeEmpties({ ...deskFilters, filter: cleanedFilter });
      window.history.replaceState(
        cleanedDeskFilters,
        '',
        `?${objectToQueryString(cleanedDeskFilters, true)}`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deskId, deskStatuses]);

  /**
   * Updates the search query parameter
   * - Updates local state
   * - Updates URL
   * - Saves to backend
   *
   * @param {String} newQuery - The new search query string
   */
  const updateQuery = useCallback(
    (newQuery) => {
      setQuery(newQuery);
      const updatedParams = { ...window.history.state, query: newQuery };

      const state = removeEmpties(updatedParams);
      const queryString = objectToQueryString(state, true);
      window.history.replaceState(state, '', `?${queryString}`);

      if (loggedIn) {
        updatePhoneSheetFilters({
          variables: {
            input: {
              phoneSheetFilters: [state],
            },
          },
        }).then((result) => {
          setDesksFilters(result?.data?.updatePhoneSheetFilters?.phoneSheetFilter);
        });
      }
    },
    [loggedIn, updatePhoneSheetFilters]
  );

  /**
   * Updates any URL parameters (filter, favorite, etc.)
   * - Merges new params with existing ones
   * - Updates local state
   * - Updates URL
   * - Saves to backend
   *
   * @param {Object} newFilters - Object containing the parameters to update (e.g., { filter: ['status1'], favorite: 1 })
   */
  const updateParams = useCallback(
    (newFilters) => {
      const updatedParams = { ...window.history.state, ...newFilters };

      const queryString = objectToQueryString(removeEmpties(updatedParams), true);
      // To parse strings that belongs to an array property, we need to parse it to object again.
      const state = removeEmpties(queryStringToObject(queryString, true));

      // Update local state
      setFilters(state.filter);
      setFavorite(state?.favorite);
      setQuery(state?.query);

      // Update URL
      window.history.replaceState(state, '', `?${queryString}`);

      // Convert arrays to comma-separated strings for backend
      const stateWithoutArrays = removeEmpties(queryStringToObject(queryString));

      // Save to backend
      if (loggedIn) {
        updatePhoneSheetFilters({
          variables: {
            input: {
              phoneSheetFilters: [stateWithoutArrays],
            },
          },
        }).then((result) => {
          setDesksFilters(result?.data?.updatePhoneSheetFilters?.phoneSheetFilter);
        });
      }
    },
    [loggedIn, updatePhoneSheetFilters]
  );

  /**
   * Changes the selected desk
   * - Clears URL params
   * - Updates deskId state
   * - This will trigger the main sync effect to load filters for the new desk
   *
   * @param {String} _deskId - The ID of the desk to switch to
   */
  const changeDesk = useCallback(
    (_deskId) => {
      if (_deskId !== deskId) {
        window.history.replaceState(undefined, '', '/');
        setDeskId(_deskId);
      }
    },
    [deskId]
  );

  /**
   * RENAME DETECTION EFFECT: Detects when status names change and updates filters accordingly
   *
   * This effect:
   * - Compares previous deskStatuses with current deskStatuses by _id
   * - Builds a map of renamed statuses (oldName -> newName)
   * - Updates filters in URL/state/backend if any match renamed statuses
   *
   * Example: Status with _id "123" changes from "ABC" -> "XYZ"
   *   If filter contains "ABC", it will be updated to "XYZ"
   */
  useEffect(() => {
    // Skip if we don't have enough data or no filters to update
    if (!previousDeskStatuses.current?.length || !deskStatuses?.length || !filters?.length) {
      // Update previous statuses for next comparison
      if (deskStatuses) {
        previousDeskStatuses.current = deskStatuses;
      }
      return;
    }

    // Build a map of renamed statuses by comparing old vs new by _id
    const statusRenameMap = {};
    previousDeskStatuses.current?.forEach((oldStatus) => {
      const newStatus = deskStatuses.find((s) => s._id === oldStatus._id);
      if (newStatus && oldStatus.status !== newStatus.status) {
        statusRenameMap[oldStatus.status] = newStatus.status;
      }
    });

    // Check if any current filter needs to be renamed
    const hasRenames = Object.keys(statusRenameMap).length > 0;
    const needsUpdate = filters.some((filter) => statusRenameMap[filter]);

    if (hasRenames && needsUpdate) {
      // Update filters with renamed status names
      const updatedFilters = filters.map((filter) => {
        return statusRenameMap[filter] || filter;
      });

      updateParams({ filter: updatedFilters });
    }

    // Update previous statuses for next comparison
    previousDeskStatuses.current = deskStatuses;
  }, [deskStatuses, filters, updateParams]);

  return {
    desk,
    search,
    deskStatuses,
    deskStatusesDeskId,
    // Actions
    changeDesk,
    updateQuery,
    refreshStore,
    reloadDeskStatuses,
    updateParams,
  };
}
